// Typed helpers built on top of the generated OpenAPI schema. Any drift between
// FastAPI Pydantic models and `schema.d.ts` is caught at `tsc --noEmit` time
// (or earlier by `npm run gen:api:check` in CI).
import type { paths } from "./schema";
import { apiGet, apiPost, apiPut } from "./client";
export { ApiError } from "./client";

type Get<P extends keyof paths, S extends 200> = paths[P] extends {
  get: { responses: { [K in S]: { content: { "application/json": infer R } } } };
}
  ? R
  : never;

type Post<P extends keyof paths, S extends 200> = paths[P] extends {
  post: { responses: { [K in S]: { content: { "application/json": infer R } } } };
}
  ? R
  : never;

type PostBody<P extends keyof paths> = paths[P] extends {
  post: { requestBody: { content: { "application/json": infer B } } };
}
  ? B
  : never;

// ── Auth (single-trader) ─────────────────────────────────────────────────────
// Reads are public; logging in sets the httpOnly fxvol_auth cookie that unlocks
// the write endpoints (require_write). `credentials:"include"` (client.ts) sends it.
export type AuthStatus = Get<"/api/v1/auth/me", 200>;
export type LoginBody = PostBody<"/api/v1/auth/login">;
export const fetchAuthMe = () => apiGet<AuthStatus>("/api/v1/auth/me");
export const postLogin = (body: LoginBody) =>
  apiPost<AuthStatus>("/api/v1/auth/login", body);
export const postLogout = () => apiPost<AuthStatus>("/api/v1/auth/logout", {});

// ── Health ────────────────────────────────────────────────────────────────
export type Health = Get<"/api/v1/health", 200>;
export type HealthExtended = Get<"/api/v1/health/extended", 200>;
export const fetchHealth = () => apiGet<Health>("/api/v1/health");
export const fetchHealthExtended = () =>
  apiGet<HealthExtended>("/api/v1/health/extended");

// ── Pricing ───────────────────────────────────────────────────────────────
export type PriceRequest = PostBody<"/api/v1/price">;
export type PriceResponse = Post<"/api/v1/price", 200>;

export const postPrice = (body: PriceRequest) =>
  apiPost<PriceResponse>("/api/v1/price", body);

// ── Vol ───────────────────────────────────────────────────────────────────
export type VolSurface = Get<"/api/v1/vol/surface", 200>;
export type TermStructure = Get<"/api/v1/vol/term-structure", 200>;
export type Smile = Get<"/api/v1/vol/smile/{tenor}", 200>;

export const fetchVolSurface = (symbol = "EURUSD") =>
  apiGet<VolSurface>("/api/v1/vol/surface", { query: { symbol } });
export const fetchTermStructure = (symbol = "EURUSD") =>
  apiGet<TermStructure>("/api/v1/vol/term-structure", { query: { symbol } });

// ── Analytics ─────────────────────────────────────────────────────────────
// Per-tenor pricing signals (CHEAP/FAIR/EXPENSIVE) and the /signals endpoint
// were retired in R9 alongside the Vol Scanner panel.
// Real OHLC candles from the market-data engine's Redis cache (IB reqHistoricalData).
// Typed by hand (not via schema.d.ts) until `npm run gen:api` is regenerated
// against the running backend; `t` = bar-open epoch ms (UTC).
export interface Bar {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
}
export const fetchBars = (symbol = "EURUSD", tf = "1h", limit = 48) =>
  apiGet<Bar[]>("/api/v1/bars", { query: { symbol, tf, limit } });

// Trade open/close events for the Performance ticker overlay (booked positions).
export interface TradeMarkerRaw {
  id: number;
  type: string;
  opened_at: string | null;
  entry_spot: number | null;
  closed_at: string | null;
  net_pnl_usd: number | null;
  state: string;
}
export const fetchTradeMarkers = (days = 30) =>
  apiGet<TradeMarkerRaw[]>("/api/v1/portfolio/trade-markers", { query: { days } });

// Cumulative greek-P&L (Taylor terms) time series for the Performance 2×2 grid.
export interface GreekPnlHistoryRaw {
  timestamp: string;
  delta_pnl_usd: number;
  gamma_pnl_usd: number;
  vega_pnl_usd: number;
  theta_pnl_usd: number;
}
export const fetchGreekPnlHistory = (window = "30d") =>
  apiGet<GreekPnlHistoryRaw[]>("/api/v1/portfolio/greek-pnl-history", { query: { window } });

// Net-liq valuation decomposition (USD cash / EUR cash $ / contracts) time series.
export interface ValuationHistoryRaw {
  timestamp: string;
  net_liq_usd: number | null;
  usd_cash_usd: number | null;
  eur_cash_usd: number | null;
  contracts_usd: number | null;
}
export const fetchValuationHistory = (window = "30d") =>
  apiGet<ValuationHistoryRaw[]>("/api/v1/portfolio/valuation-history", { query: { window } });

// ── R11 voldesk wiring (read) ───────────────────────────────────────────────
// Typed against schema.d.ts. Endpoints returning a bare dict server-side surface
// here as opaque values; the per-domain adapter (voldesk/data/live/*) maps them
// to the DATA/DATA2 shapes the views consume.

// Regime
export const fetchRegimeEvents = (n = 10, pastDays = 0) =>
  apiGet<unknown>("/api/v1/regime/events", { query: { n, past_days: pastDays } });

// PCA signals
export type PcaState = Get<"/api/v1/signals/pca/state", 200>;
export const fetchPcaState = (symbol = "EURUSD") =>
  apiGet<PcaState>("/api/v1/signals/pca/state", { query: { symbol } });
export type PcaHistory = Get<"/api/v1/signals/pca/history", 200>;
export const fetchPcaHistory = (pcId: number, n = 120, symbol = "EURUSD") =>
  apiGet<PcaHistory>("/api/v1/signals/pca/history", { query: { symbol, pc_id: pcId, n } });

// Positions (Step 5)
export const fetchOpenPositions = () => apiGet<unknown>("/api/v1/positions/open");

// Portfolio panels (A–J + scenarios)
export const fetchPortfolioAccount = () => apiGet<unknown>("/api/v1/portfolio/account");
export const fetchPortfolioCash = () => apiGet<unknown>("/api/v1/portfolio/cash");
export const fetchPortfolioDailyPnl = (days = 90) =>
  apiGet<unknown>("/api/v1/portfolio/daily-pnl", { query: { days } });
export const fetchPortfolioStats = () => apiGet<unknown>("/api/v1/portfolio/stats");
export const fetchPortfolioVar = () => apiGet<unknown>("/api/v1/portfolio/var");
export const fetchRiskPerTenor = () => apiGet<unknown>("/api/v1/portfolio/risk-per-tenor");
export const fetchEquityCurve = (window = "30d") =>
  apiGet<unknown>("/api/v1/portfolio/equity-curve", { query: { window } });
export const fetchVegaPerTenor = () =>
  apiGet<unknown>("/api/v1/portfolio/vega-per-tenor");
export const fetchStressGrid = (axis = "spot-vol", output = "pnl") =>
  apiGet<unknown>("/api/v1/portfolio/stress-grid", { query: { axis, output } });
export const fetchGreeksLadder = (axis = "spot") =>
  apiGet<unknown>("/api/v1/portfolio/greeks-ladder", { query: { axis } });
export const fetchPnlAttribution = (lookbackHours = 24) =>
  apiGet<unknown>("/api/v1/portfolio/pnl-attribution", {
    query: { lookback_hours: lookbackHours },
  });

// Same decomposition, bucketed by an axis (tenor / structure) → P&L-attribution matrix.
export const fetchPnlAttributionMatrix = (groupBy = "tenor", lookbackHours = 24) =>
  apiGet<unknown>("/api/v1/portfolio/pnl-attribution", {
    query: { group_by: groupBy, lookback_hours: lookbackHours },
  });
export const fetchPinRisk = () => apiGet<unknown>("/api/v1/portfolio/pin-risk");
export const fetchMarginalVar = () => apiGet<unknown>("/api/v1/portfolio/marginal-var");

// Trade (read)
// Spot FX (EUR.USD cash) market order — write-gated proxy to the
// execution-engine (/internal/orders). qty = base-currency notional.
export interface SpotOrderBody {
  symbol: string;
  sec_type: "CASH";
  side: "BUY" | "SELL";
  qty: number;
  exchange: string;
  currency: string;
}
export const postSpotOrder = (body: SpotOrderBody) =>
  apiPost<unknown>("/api/v1/orders", body);
export const fetchTradeLimits = () => apiGet<unknown>("/api/v1/trade/limits");
export const fetchTradeBook = (symbol = "EURUSD") =>
  apiGet<unknown>("/api/v1/trade/book", { query: { symbol } });

// Admin config (read; write lands in Phase 2 behind auth)
export type ConfigResponse = Get<"/api/v1/admin/config", 200>;
export const fetchConfig = () => apiGet<ConfigResponse>("/api/v1/admin/config");
export const fetchConfigHistory = (limit = 50) =>
  apiGet<unknown>("/api/v1/admin/config/history", { query: { limit } });
// Settings write (Phase 2 / 2w) — gated by auth in prod (require_write), free locally.
export const revertConfig = (version: number, comment?: string) =>
  apiPost<unknown>(`/api/v1/admin/config/revert/${version}`, { user: "trader", comment });
export const putConfig = (patch: Record<string, unknown>, comment?: string) =>
  apiPut<unknown>("/api/v1/admin/config", { patch, user: "trader", comment });
export const fetchDomainSettings = (domain: string) =>
  apiGet<unknown>(`/api/v1/admin/settings/${domain}`);
export const putDomainSettings = (domain: string, updates: Record<string, number>) =>
  apiPut<unknown>(`/api/v1/admin/settings/${domain}`, { updates, user: "trader" });

// ── Trade write (Phase 2 / 6w) — submit + close ──────────────────────────────
// Paper-first: `execution_mode:"live"` routes to the IB *paper* account (the
// system stays READ_ONLY_API until an explicit go-live, see docs/strategy.md §5).
// Gated by auth in prod (require_write); free locally behind VITE_WRITE_ENABLED.

/** One free-composed leg sent to POST /trade/preview (mirrors backend LegSpec). */
export interface PreviewLeg {
  contract_type: "call" | "put" | "future";
  side: "BUY" | "SELL";
  tenor: string;
  delta_pillar?: string;                 // 10dp/25dp/atm/25dc/10dc (options)
  strike?: number;                       // explicit strike override (options)
  qty_factor?: number;                   // relative weight; ×base_qty at sizing
  future_contract_size?: "full" | "micro";
}

/** Subset of the /trade/preview payload the desk reads (server returns a dict). */
export interface TradePreview {
  preview_id: string;
  state: string;                         // "valid_for_submit" | "blocked" | …
  blocking_reasons?: string[];
  structure?: { type?: string };
  pricing?: {
    premium_paid_usd?: number;
    max_loss_usd?: number;
    max_loss_at_expiry_only?: boolean;
  };
  greeks_net?: Record<string, number | Record<string, number>>;
}

export const createTradePreview = (legs: PreviewLeg[], qty: number, symbol = "EURUSD") =>
  apiPost<TradePreview>("/api/v1/trade/preview", { legs, qty }, { query: { symbol } });

/** Submit a previewed structure. execution_mode "live" = IB paper account. */
export const submitTrade = (previewId: string, executionMode: "live" | "mock" = "live") =>
  apiPost<Record<string, unknown>>("/api/v1/trade/submit", {
    preview_id: previewId,
    execution_mode: executionMode,
  });

// Persisted submitted structures (DB: trade_structure + booked_position). Read-only,
// so the Orders blotter survives refresh and reflects the real DB state.
export interface SubmittedLeg {
  leg_idx: number;
  contract: string | null; // IB localSymbol of the leg ("EUUV6 C1130")
  contract_type: string | null; // "call" / "put" / "future"
  strike: number | null;
  side: string | null; // "BUY" / "SELL"
  qty: number; // ordered contracts for this leg
  qty_filled: number; // filled so far
  state: string; // per-leg order state
}
export interface SubmittedTrade {
  id: number;
  created_at: string;
  structure_type: string | null;
  product_label: string | null;
  contract: string | null; // IB localSymbol(s) of the legs ("EUUV6 C1130", or "… +N")
  reference_tenor: string | null;
  base_qty: number | null;
  qty_total?: number | null; // total contracts across the structure's legs
  qty_filled?: number | null; // contracts filled so far (for in-flight progress)
  legs?: SubmittedLeg[]; // per-leg detail — the blotter renders one row per leg
  state: string | null;
  execution_mode: string | null;
  position_state: string | null;
  order_role: string | null; // entry / closing / unwind / hedge
  closes_trade_id?: number | null; // for a close: the original trade it closes (#30, not this #31)
}
export const fetchSubmitted = (limit = 50) =>
  apiGet<SubmittedTrade[]>("/api/v1/trade/submitted", { query: { limit } });

// Booking-driven positions: structures grouped by trade_structure (real product +
// tenor + legs), live marks attached, plus IB positions not tied to any booking.
export interface StructuredLeg {
  leg_idx: number; contract_type: string; side: string; qty: number;
  strike: number | null; expiry: string | null; state: string; qty_filled: number;
  ib_local_symbol: string | null; linked: boolean;
  // open = really held per the BOOK (leg_position), even when the netted IB mirror
  // can't show it (two trades on opposite sides of one contract net to 0 at IB).
  // open_qty is the signed book holding. Render on `open`, not `linked`.
  open?: boolean; open_qty?: number | null;
  mark: number | null; pnl_usd: number | null;
  delta_usd: number | null; gamma_usd: number | null; vega_usd: number | null;
  theta_usd: number | null; vanna_usd: number | null; volga_usd: number | null; iv: number | null;
  // Live IB-mirror identity (present only when `linked`). position_id is the
  // open_position row the UI closes by.
  position_id: number | null; con_id: number | null; held_qty: number | null; held_side: string | null;
  entry: number | null; nominal_eur: number | null; tenor: string | null;
  opened: string | null; updated: string | null;
}
export interface StructuredStructure {
  structure_id: number; structure_type: string; product_label: string | null;
  tenor: string; state: string; base_qty: number; created_at: string | null;
  legs: StructuredLeg[];
  net: { delta_usd: number; gamma_usd: number; vega_usd: number; theta_usd: number; pnl_usd: number; n_linked: number };
}
export interface UnlinkedPosition {
  id: number; symbol: string; product_label: string | null; side: string;
  qty: number | null; tenor: string | null; expiry: string | null;
  mark: number | null; pnl_usd: number | null; delta_usd: number | null;
  gamma_usd: number | null; vega_usd: number | null; theta_usd: number | null;
  vanna_usd: number | null; volga_usd: number | null; iv: number | null;
}
export interface StructuredPositions { structures: StructuredStructure[]; unlinked: UnlinkedPosition[] }
export const fetchStructuredPositions = () =>
  apiGet<StructuredPositions>("/api/v1/positions/structured");

/** Close a single leg (OpenPosition.id). Partial close via `qty`. */
export const closeContract = (positionId: number, qty: number) =>
  apiPost<Record<string, unknown>>(`/api/v1/positions/${positionId}/close`, { qty });

/** Close every open leg of a trade (OpenPosition.trade_id). */
export const closeTrade = (tradeId: number) =>
  apiPost<Record<string, unknown>>(`/api/v1/trades/${tradeId}/close`, {});

/** Cancel every non-terminal order of a trade at IB + terminalise the structure. */
export const cancelTrade = (structureId: number) =>
  apiPost<Record<string, unknown>>(`/api/v1/trades/${structureId}/cancel`, {});

// Dev / system
export const fetchDevEngines = () => apiGet<unknown>("/api/v1/dev/engines");
export const fetchCycleProgress = () => apiGet<unknown>("/api/v1/dev/cycle-progress");
